<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :header="t('campaignsBulkList.groupedActions.publishToGalleryDialogHeader')"
    :style="{ width: '550px' }"
  >
    <div class="p-fluid">
      <p class="mb-3">
        {{ t('campaignsBulkList.groupedActions.publishToGalleryDialogMessage', { count: campaigns.length }) }}
      </p>

      <div class="publish-gallery-campaigns">
        <div
          v-for="campaign in campaigns"
          :key="campaign.id"
          class="publish-gallery-campaign-row"
        >
          <span class="publish-gallery-campaign-name">{{ campaign.name }}</span>
          <div class="publish-gallery-campaign-toggle">
            <label class="publish-gallery-toggle-label">
              {{ t('campaignsBulkList.groupedActions.includeTemplates') }}
            </label>
            <InputSwitch v-model="campaign.includeTemplates" />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <SpmButton
        :label="t('cancel')"
        icon="far fa-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('campaignsBulkList.groupedActions.publishToGallery')"
        icon="fa fa-cloud-arrow-up"
        class-style="p-button p-button-primary"
        :loading="isLoading"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';

interface CampaignEntry {
  id: number;
  name: string;
  includeTemplates: boolean;
}

export default defineComponent({
  name: 'PublishToGalleryDialog',

  components: {
    Dialog,
    InputSwitch,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    const isVisible = ref(false);
    const isLoading = ref(false);
    const campaigns = ref<CampaignEntry[]>([]);

    /**
     * Open the dialog with the list of campaign IDs and names
     */
    const open = (campaignList: { id: number; name: string }[]) => {
      campaigns.value = campaignList.map((c) => ({
        id: c.id,
        name: c.name,
        includeTemplates: true,
      }));
      isVisible.value = true;
    };

    const close = () => {
      isVisible.value = false;
      campaigns.value = [];
      isLoading.value = false;
    };

    const confirm = () => {
      const campaignIds = campaigns.value.map((c) => c.id);
      const campaignIdsWithoutTemplates = campaigns.value
        .filter((c) => !c.includeTemplates)
        .map((c) => c.id);

      isLoading.value = true;
      emit('confirm', { campaignIds, campaignIdsWithoutTemplates });
    };

    /**
     * Called by parent after publish completes (success or error)
     */
    const done = () => {
      close();
    };

    expose({ open, done });

    return {
      t,
      isVisible,
      isLoading,
      campaigns,
      close,
      confirm,
    };
  },
});
</script>

<style scoped>
.publish-gallery-campaigns {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.publish-gallery-campaign-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--surface-border, #dee2e6);
  border-radius: 6px;
  background: var(--surface-ground, #f8f9fa);
}

.publish-gallery-campaign-name {
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 1rem;
}

.publish-gallery-campaign-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.publish-gallery-toggle-label {
  font-size: 0.85rem;
  color: var(--text-color-secondary, #6c757d);
  white-space: nowrap;
}
</style>
